import { Stack, Typography } from '@mui/material';
import { GridColDef, GridRow } from '@mui/x-data-grid-pro';

import AppLink from '@/components/AppLink';
import DataGrid from '@/components/DataGrid';
import { BasetenPageEnum } from '@/routes/types';
import VersionsCountByStatus from '@/sections/Workspace/VersionsCountByStatus';
import { getVersionsByStatus, sortVersionsByStatus } from '@/sections/Workspace/utils';
import { dateDisplay } from '@/utils/date';
import moment from 'moment';

import ProductionStatus from '../../sections/ProductionStatus';
import { dataGridProps, dataGridStyles } from '../../styled';
import Actions from '../Actions';
import { WorkspaceModelFragment } from '../__generated__/fragments.generated';
import { usePinnedAPI } from '../hooks';
import { ListProps } from '../types';

const ROW_HEIGHT = 67;

function Table({ models, height }: ListProps) {
  const { getPinActionItem } = usePinnedAPI();

  const columns: GridColDef<WorkspaceModelFragment>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: ({ row }) => {
        const displayTime = dateDisplay(moment(row.lastActivity));

        return (
          <Stack width="100%" gap={0.5}>
            <Stack gap={0.5} direction="row" alignItems="center">
              <Typography noWrap variant="h5">
                {row.name}
              </Typography>
              <Typography color="text.muted">{row.id}</Typography>
            </Stack>
            <Typography variant="body2" color="text.subtle">
              last activity {displayTime}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Production',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => <ProductionStatus primary={row.primaryVersion} />,
    },
    {
      field: 'versions',
      headerName: 'Deployments',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => {
        return (
          <VersionsCountByStatus
            options={sortVersionsByStatus(
              getVersionsByStatus(row.versionCountByStatus, undefined, true),
            )}
          />
        );
      },
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 110,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" gap={0.2}>
            {getPinActionItem(row.id, row.name)}
            <Actions modelId={row.id} modelName={row.name} variant="minimal" />
          </Stack>
        );
      },
    },
  ];

  return (
    <Stack data-cy="model-table-content" height={height}>
      <DataGrid<WorkspaceModelFragment>
        rows={models}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        getRowId={(row) => row.id}
        sx={dataGridStyles}
        disableAutoHeight
        {...dataGridProps}
        slots={{
          row: (props) => (
            <AppLink
              page={BasetenPageEnum.Model}
              params={{ modelId: props.row.id }}
              key={props.row}
              underline="none"
              sx={{ color: 'inherit', '&:hover': { textDecoration: 'none' } }}
            >
              <GridRow {...props} />
            </AppLink>
          ),
        }}
      />
    </Stack>
  );
}

export default Table;
